import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { notify } from "@/utils";

import type { GroupParticipant } from "../../services/group";
import {
  useFetchGroupDetails,
  useFetchGroupParticipants,
  useSyncGroupParticipants,
} from "../../services/group";
import GroupAddUsersPage from "./GroupAddUsersPage";
import GroupEditPage from "./GroupEditPage";
import GroupInviteLinkModal from "./GroupInviteLinkModal";
import GroupMemberActions from "./GroupMemberActions";
import GroupMemberRow from "./GroupMemberRow";
import GroupProfileSection from "./GroupProfileSection";
import GroupSettingsHeader from "./GroupSettingsHeader";
import { GroupSettingsSkeleton } from "./GroupUserListSkeleton";

type GroupSettingsPageProps = {
  groupId: number;
  fallbackName?: string;
  onBack: () => void;
  onLeftGroup?: () => void;
};

export default function GroupSettingsPage({
  groupId,
  fallbackName,
  onBack,
  onLeftGroup,
}: GroupSettingsPageProps) {
  const { t } = useTranslation("messenger.user");
  const [view, setView] = useState<"settings" | "edit">("settings");
  const [memberSearchQuery, setMemberSearchQuery] = useState("");
  const [showMemberSearch, setShowMemberSearch] = useState(false);
  const [showAddUsers, setShowAddUsers] = useState(false);
  const [showInviteLink, setShowInviteLink] = useState(false);

  const { data: groupDetails, isLoading: isGroupLoading } =
    useFetchGroupDetails(groupId);
  const { data: participants, isLoading: isParticipantsLoading } =
    useFetchGroupParticipants(groupId);
  const syncParticipants = useSyncGroupParticipants(groupId);

  const groupName = groupDetails?.name ?? fallbackName ?? "";
  const memberCount = participants?.total ?? participants?.data?.length ?? 0;

  const participantIds = useMemo(
    () => new Set(participants?.data?.map((p) => p.id) ?? []),
    [participants?.data],
  );

  const members = useMemo(() => {
    const ownerId = groupDetails?.owner?.id;
    const data = (participants?.data ?? []).map((p) => ({
      ...p,
      is_owner: p.is_owner || p.id === ownerId,
    }));
    const hasOwner = !ownerId || data.some((p) => p.id === ownerId);
    const list: GroupParticipant[] = hasOwner
      ? data
      : [
          { ...groupDetails!.owner, is_owner: true } as GroupParticipant,
          ...data,
        ];
    const sorted = [...list].sort(
      (a, b) => Number(b.is_owner) - Number(a.is_owner),
    );

    if (!memberSearchQuery.trim()) return sorted;

    const query = memberSearchQuery.trim().toLowerCase();
    return sorted.filter((p) => {
      const fullName = `${p.first_name} ${p.last_name}`.toLowerCase();
      return (
        fullName.includes(query) ||
        p.personnel_code?.toLowerCase().includes(query)
      );
    });
  }, [groupDetails, participants?.data, memberSearchQuery]);

  const inviteUrl = groupDetails?.invite_token
    ? `${window.location.origin}/messenger/groups/join/${groupDetails.invite_token}`
    : "";

  const handleCopyInviteLink = () => {
    if (!inviteUrl) return;
    navigator.clipboard.writeText(inviteUrl);
    notify.success(t("Link copied to clipboard"));
  };

  const handleAddUsers = (newUserIds: number[]) => {
    const existingUserIds = participants?.data?.map((p) => p.id) ?? [];
    syncParticipants.mutate(
      { user_ids: [...existingUserIds, ...newUserIds] },
      {
        onSuccess: () => {
          notify.success(t("Users added successfully"));
          setShowAddUsers(false);
        },
        onError: () => notify.error(t("Failed to add users")),
      },
    );
  };

  if (view === "edit" && groupDetails?.can_update === true) {
    return (
      <GroupEditPage
        groupId={groupId}
        fallbackName={fallbackName}
        onBack={() => setView("settings")}
        onDeleted={onLeftGroup}
      />
    );
  }

  if ((isGroupLoading || isParticipantsLoading) && view === "settings") {
    return <GroupSettingsSkeleton />;
  }

  if (showAddUsers) {
    return (
      <GroupAddUsersPage
        participantIds={participantIds}
        onBack={() => setShowAddUsers(false)}
        onConfirm={handleAddUsers}
        isSubmitting={syncParticipants.isPending}
      />
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden bg-white">
      <GroupSettingsHeader
        onBack={onBack}
        showSearch={showMemberSearch}
        onToggleSearch={() => {
          setShowMemberSearch((value) => !value);
          if (showMemberSearch) setMemberSearchQuery("");
        }}
        searchQuery={memberSearchQuery}
        onSearchChange={setMemberSearchQuery}
        canUpdate={groupDetails?.can_update === true}
        onEdit={() => setView("edit")}
      />

      <GroupProfileSection
        name={groupName}
        imageThumb={groupDetails?.image_thumb}
        memberCount={memberCount}
      />

      <div className="bg-gray-50 px-6 py-2 text-sm font-medium text-gray-600">
        {t("Group members")}
      </div>

      {groupDetails?.can_update === true && (
        <GroupMemberActions
          onAddUsers={() => setShowAddUsers(true)}
          onInviteLink={() => setShowInviteLink(true)}
        />
      )}

      <div className="min-h-0 flex-1 overflow-y-auto">
        {members.map((participant) => (
          <GroupMemberRow key={participant.id} participant={participant} />
        ))}
      </div>

      <GroupInviteLinkModal
        open={showInviteLink}
        onClose={() => setShowInviteLink(false)}
        inviteUrl={inviteUrl}
        onCopy={handleCopyInviteLink}
      />
    </div>
  );
}
